import React from 'react'
import { rootUseStyles } from '../../../taskpane/fluent-style';
import { AlertBox } from '../../../components/Alert/AlertBox';
import ThreatBoxIndicator from '../../../components/Alert/ThreatBoxIndicator';
import { useIsScanning, useResultStore } from '../../../providers/ResultProvider';
import { Text } from '@fluentui/react-components';
import { ThreatType } from '../../../components/threat/threat.config';
import { ScanResultByGroupIdType } from '../../../types';

// The threats array should use the ThreatType for type safety
const threats: ThreatType[] = ["Malicious", "Suspicious", "Clean", "Unrated"];

interface ThreatDescriptionsProps {
    threatItems?: Partial<ScanResultByGroupIdType>;
    indicatorStatus?: any;
    isScanning?: boolean;
}

export default function ThreatDescriptions({
    threatItems: propThreatItems,
    indicatorStatus: propIndicatorStatus,
    isScanning: propIsScanning
}: ThreatDescriptionsProps) {
    const storeIsScanning = useIsScanning();
    const storeThreatItems = useResultStore((s) => s.threatItems);
    const storeIndicatorStatus = useResultStore((s) => s.indicatorStatus);

    const isScanning = propIsScanning !== undefined ? propIsScanning : storeIsScanning;
    const threatItems = propThreatItems || storeThreatItems;
    const indicatorStatus = propIndicatorStatus || storeIndicatorStatus;

    const statusCounts = getStatusCounts(threatItems || {});

    return (
        <>
            <Text
                as="p"
                style={{
                    color: "#7B7B7B",
                    fontSize: "12px",
                    fontWeight: 500,
                    padding: "2px"
                }}
                className={rootUseStyles().paragraph}
            >
                {isScanning ? "A deep scan is currently in progress..." : "Scan Complete"}
            </Text>
            {isScanning && (
                <AlertBox
                    type="info"
                    title="Scanning in the background"
                    description="This may take a moment. Feel free to keep browsing your email, you can come back anytime to check your results."
                />
            )}
            {!isScanning && indicatorStatus?.["deep-scan"] && (
                <AlertBox
                    type={indicatorStatus?.["deep-scan"]?.status}
                    title={indicatorStatus?.["deep-scan"]?.status}
                    description={indicatorStatus?.["deep-scan"]?.description}
                />
            )}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                }}
            >
                {threats.map((threat, index) => (
                    <ThreatDetails
                        key={index}
                        title={threat}
                        count={statusCounts[threat] || 0}
                    />
                ))}
            </div>
        </>
    );
}

// Helper function to count the totals
function getStatusCounts(data: Partial<ScanResultByGroupIdType>) {
    const counts: Record<ThreatType, number> = {
        Malicious: 0,
        Suspicious: 0,
        Clean: 0,
        Unrated: 0,
        // Add other threat types if they exist in your config
        UNKNOWN: 0,
        ERROR_NORMALIZING: 0,
        NOT_APPLICABLE: 0,
        default: 0
    };
    // Iterate over all the arrays in the data object
    Object.values(data).forEach((threatItems) => {
        if (Array.isArray(threatItems)) {
            threatItems.forEach((item) => {
                if (item?.status) {
                    // API returns lowercase ("unrated", "clean") — normalize to match ThreatType casing
                    const normalized = (item.status.charAt(0).toUpperCase() + item.status.slice(1)) as ThreatType;
                    counts[normalized] = (counts[normalized] || 0) + 1;
                }
            });
        }
    });

    return counts;
}

function ThreatDetails({ title, count }: { title: ThreatType; count: number }) {
    // Get the deepScanData from the store

    return (
        <ThreatBoxIndicator
            label={title}
            // Use the calculated count
            value={count || 0}
        />
    );
}